/**
 * Busy-queue position and ETA (decision 84): when a transcription can't start at once (jobs ahead of it, or Groq's
 * limits make the governor wait), the request still succeeds (queued) and says roughly when it will be done. The ETA is
 * the pending audio seconds ahead (plus its own) divided by the combined capacity the Groq orgs have left, or the
 * requests ahead divided by their combined request rate, whichever is longer. Pure; the API fills the numbers.
 */

export interface QueueInfo {
  /** 1 = next. */
  position: number;
  /** Whole minutes, at least 1. */
  etaMinutes: number;
}

/**
 * A job that would wait at least this long before it STARTS (work ahead of it, or no org free until later) is in the
 * busy queue: the request says so. Its own length never makes it "busy".
 */
export const BUSY_START_MINUTES = 1;

export interface QueueCapacity {
  /** Audio seconds per minute all orgs together can take now. */
  audioSPerMin: number;
  /** Requests per minute all orgs together can take now. */
  requestsPerMin: number;
  /** Until the first org can take anything (0 = now; e.g. the day's allowance is used up until midnight UTC). */
  waitMs: number;
}

/** Position and ETA from what is ahead (jobs and their audio seconds) and the orgs' capacity. */
export function estimateQueue(o: { ahead: number; aheadAudioS: number; ownAudioS: number; capacity: QueueCapacity }): QueueInfo & { startMinutes: number; busy: boolean } {
  const { capacity: c } = o;
  const minutesFor = (audioS: number, requests: number) => {
    const byAudio = c.audioSPerMin > 0 ? audioS / c.audioSPerMin : Infinity;
    const byRequests = c.requestsPerMin > 0 ? requests / c.requestsPerMin : Infinity;
    const work = Math.max(byAudio, byRequests);
    // No capacity at all (every org off or used up): an hour as a cautious guess.
    return Number.isFinite(work) ? work : 60;
  };
  const wait = c.waitMs / 60_000;
  const startMinutes = wait + (o.ahead > 0 ? minutesFor(o.aheadAudioS, o.ahead) : 0);
  const minutes = wait + minutesFor(o.aheadAudioS + o.ownAudioS, o.ahead + 1);
  return { position: o.ahead + 1, etaMinutes: Math.max(1, Math.ceil(minutes)), startMinutes, busy: startMinutes >= BUSY_START_MINUTES };
}

/** Rounds an ETA the way people say it: 1–10 exact, then to 5, then to 15 above an hour. */
export function roundEta(minutes: number): number {
  const m = Math.max(1, Math.ceil(minutes));
  if (m <= 10) return m;
  if (m <= 60) return Math.ceil(m / 5) * 5;
  return Math.ceil(m / 15) * 15;
}

const etaText = (minutes: number) => {
  const m = roundEta(minutes);
  if (m < 90) return `about ${m} minute${m === 1 ? '' : 's'}`;
  const h = Math.round((m / 60) * 2) / 2;
  return `about ${h} hour${h === 1 ? '' : 's'}`;
};

/**
 * What the app (ReelInspector, Transcribe all), the extension's side panel and MCP say while a transcription waits in
 * the busy queue (decision 84). `count` > 1 for several reels.
 */
export function busyQueueMessage(etaMinutes: number, count = 1): string {
  return count > 1
    ? `Our servers are busy right now — your reels are in the queue and will be transcribed in ${etaText(etaMinutes)}.`
    : `Our servers are busy right now — your reel is in the queue and will be transcribed in ${etaText(etaMinutes)}.`;
}
